import {
  Box,
  Divider,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import { format, isValid } from "date-fns";
import { MailCheck, MessageSquare } from "lucide-react";
import type { QueryResponseEntry } from "../reviewLogic";

const fmtDate = (value: string | null): string | null => {
  if (!value) return null;
  const d = new Date(value);
  return isValid(d) ? format(d, "d MMM yyyy") : null;
};

interface QueryResponsesModalProps {
  isOpen: boolean;
  onClose: () => void;
  entries: QueryResponseEntry[];
}

/**
 * QueryResponsesModal (PLO-4803) — lists the IAM team's answers to the
 * reviewer's queries that are back awaiting a re-decision. Per entry: the
 * account, the original question, the answer, and who answered it + when.
 * `answeredBy` is omitted on older payloads that predate the field — the
 * "Answered by" line falls back to just the date.
 */
const QueryResponsesModal = ({
  isOpen,
  onClose,
  entries,
}: QueryResponsesModalProps) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg" scrollBehavior="inside">
      <ModalOverlay bg="rgba(0, 0, 0, 0.3)" backdropFilter="blur(3px)" />
      <ModalContent borderRadius="12px" boxShadow="lg" maxH="80vh">
        <ModalHeader
          borderBottom="1px solid"
          borderColor="border.default"
          py={4}
          px={5}
        >
          <HStack spacing="sm" align="center">
            <Icon as={MailCheck} boxSize="18px" color="p.600" />
            <VStack align="flex-start" spacing={0}>
              <Text fontSize="15px" fontWeight="600" color="text.body.primary">
                Query responses
              </Text>
              <Text fontSize="13px" color="text.body.tertiary">
                {entries.length}{" "}
                {entries.length === 1 ? "response" : "responses"} from the IAM
                team awaiting your re-decision
              </Text>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton top={3} right={3} />
        <ModalBody py={4} px={5}>
          <VStack spacing={3} align="stretch">
            {entries.length === 0 ? (
              <Text
                fontSize="13px"
                color="text.body.tertiary"
                textAlign="center"
                py={6}
              >
                No responses yet.
              </Text>
            ) : (
              entries.map((entry) => {
                const resolved = fmtDate(entry.resolvedAt);
                const answeredLabel = entry.answeredBy
                  ? [`Answered by ${entry.answeredBy}`, resolved]
                      .filter(Boolean)
                      .join(" · ")
                  : resolved
                    ? `Answered ${resolved}`
                    : null;
                return (
                  <Box
                    key={entry.accountId}
                    borderWidth="1px"
                    borderColor="border.default"
                    borderRadius="10px"
                    p={4}
                  >
                    <HStack justify="space-between" align="baseline" mb={2}>
                      <VStack align="flex-start" spacing={0} minW={0}>
                        <Text
                          fontSize="sm"
                          fontWeight="600"
                          color="text.body.primary"
                          noOfLines={1}
                        >
                          {entry.name}
                        </Text>
                        {entry.email && (
                          <Text
                            fontSize="xs"
                            color="text.body.tertiary"
                            noOfLines={1}
                          >
                            {entry.email}
                          </Text>
                        )}
                      </VStack>
                      {answeredLabel && (
                        <Text
                          fontSize="xs"
                          color="text.body.tertiary"
                          flexShrink={0}
                        >
                          {answeredLabel}
                        </Text>
                      )}
                    </HStack>

                    {entry.query && (
                      <HStack align="flex-start" spacing={2} mb={2}>
                        <Icon
                          as={MessageSquare}
                          boxSize="14px"
                          color="text.body.tertiary"
                          mt="2px"
                          flexShrink={0}
                        />
                        <Text fontSize="13px" color="text.body.secondary">
                          {entry.query}
                        </Text>
                      </HStack>
                    )}

                    <Divider borderColor="border.subtle" my={2} />

                    <HStack align="flex-start" spacing={2}>
                      <Icon
                        as={MailCheck}
                        boxSize="14px"
                        color="p.600"
                        mt="2px"
                        flexShrink={0}
                      />
                      <Text fontSize="13px" color="text.body.primary">
                        {entry.answer || "-"}
                      </Text>
                    </HStack>
                  </Box>
                );
              })
            )}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default QueryResponsesModal;
